/**
 * FeatureFlags.jsx
 * ═══════════════════════════════════════════════════════════════════════════════
 * React Context provider for feature flags.
 * Fetches flags from /api/featureflags and provides them to all components.
 * 
 * USAGE:
 * 
 * 1. Wrap your app in <FeatureFlagsProvider>:
 * 
 *    import { FeatureFlagsProvider } from './components/FeatureFlags';
 * 
 *    function App() {
 *        return (
 *            <FeatureFlagsProvider>
 *                <YourApp />
 *            </FeatureFlagsProvider>
 *        );
 *    }
 * 
 * 2. Use the hook in any component:
 * 
 *    import { useFeatureFlags } from './components/FeatureFlags';
 * 
 *    function MyComponent() {
 *        const flags = useFeatureFlags();
 *        
 *        return (
 *            <>
 *                {flags.chatHistory && <ChatHistoryPanel />}
 *                {flags.createSpark && <CreateSparkButton />}
 *                {flags.voice && <VoiceMicButton />}
 *                {flags.workplaceIntegrations.enabled && (
 *                    <>
 *                        {flags.workplaceIntegrations.teams && <TeamsIntegration />}
 *                        {flags.workplaceIntegrations.outlook && <OutlookIntegration />}
 *                    </>
 *                )}
 *                {flags.sharing.enabled && (
 *                    <>
 *                        {flags.sharing.email && <EmailButton />}
 *                        {flags.sharing.teams && <TeamsButton />}
 *                    </>
 *                )}
 *            </>
 *        );
 *    }
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import ApiClient from "../services/ApiClient";

// ── Default flags (fallback if API fails) ──────────────────────────────────────
// 🔥 HARDCODED FOR TESTING - bypasses API completely
const DEFAULT_FLAGS = {
    chatHistory: true,
    createSpark: false,
    contentGeneration: false,
    workplaceIntegrations: {
        enabled: false,
        teams: true,
        outlook: true,
        sharepoint: true,
        onedrive: true,
        ado: false,
        calendar: true
    },
    agents: false,
    workplaceAgentFramework: {
        enabled: true,
        teams: true,
        outlook: true,
        onedrive: true,
        calendar: true,
        allowSelectAll: true,
        requireHumanValidation: true,
        useSparkConsultation: true
    },
    voice: true,
    kenshoGoogleSwitch: false,
    sparkStore: {
        enabled: true,
        embedIcon: false
    },
    sharing: {
        enabled: true,
        email: true,
        teams: true,
        onedrive: true,
        ado: false
    },
    sparkWidget: {
        enabled: true,
        authentication: true,
        sparkPicker: true
    },
    analyticsDashboard: false,    // Show EWI Platform Analytics Dashboard icon
    isLoading: false,
    error: null
};

// ── Context ────────────────────────────────────────────────────────────────────
const FeatureFlagsContext = createContext(DEFAULT_FLAGS);

// ── Provider ───────────────────────────────────────────────────────────────────
export function FeatureFlagsProvider({ children }) {
    const [flags, setFlags] = useState(DEFAULT_FLAGS);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;

        // ✅ FETCH FLAGS FROM API - Admin panel updates will be reflected!
        (async () => {
            try {
                console.log('[FeatureFlags] Fetching feature flags from API...');

                const data = await ApiClient.get("/api/featureflags");
                console.log('[FeatureFlags] API response:', data);

                if (!mounted) return;

                // Parse server response into flat structure for easy component usage
                const parsedFlags = parseFeatureFlags(data);
                console.log('[FeatureFlags] Parsed flags:', parsedFlags);

                setFlags({ ...parsedFlags, isLoading: false, error: null });
                setIsLoading(false);
            } catch (err) {
                console.error('[FeatureFlags] Failed to load:', err);

                if (!mounted) return;

                // Use defaults on error
                setError(err.message);
                setFlags({ ...DEFAULT_FLAGS, isLoading: false, error: err.message });
                setIsLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, []);

    return (
        <FeatureFlagsContext.Provider value={{ ...flags, isLoading, error }}>
            {children}
        </FeatureFlagsContext.Provider>
    );
}

// ── Hook ───────────────────────────────────────────────────────────────────────
export function useFeatureFlags() {
    const context = useContext(FeatureFlagsContext);
    
    if (!context) {
        console.warn('[FeatureFlags] useFeatureFlags called outside provider, using defaults');
        return DEFAULT_FLAGS;
    }
    
    return context;
}

// ── Helper: Parse server response ──────────────────────────────────────────────
function parseFeatureFlags(data) {
    try {
        const f = data?.features || {};

        return {
            // Top-level features — use ?? false so a missing field never silently re-enables a flag
            chatHistory:        f.chatHistory?.enabled        ?? false,
            createSpark:        f.createSpark?.enabled        ?? false,
            contentGeneration:  f.contentGeneration?.enabled  ?? false,
            agents:             f.agents?.enabled             ?? false,
            workplaceAgentFramework: {
                enabled:                f.workplaceAgentFramework?.enabled                         ?? false,
                teams:                  f.workplaceAgentFramework?.agents?.teams?.enabled          ?? true,
                outlook:                f.workplaceAgentFramework?.agents?.outlook?.enabled        ?? true,
                onedrive:               f.workplaceAgentFramework?.agents?.onedrive?.enabled       ?? true,
                calendar:               f.workplaceAgentFramework?.agents?.calendar?.enabled       ?? true,
                allowSelectAll:         f.workplaceAgentFramework?.allowSelectAll                  ?? true,
                requireHumanValidation: f.workplaceAgentFramework?.requireHumanValidation          ?? true,
                useSparkConsultation:   f.workplaceAgentFramework?.useSparkConsultation            ?? true
            },
            voice:              f.voice?.enabled              ?? false,
            kenshoGoogleSwitch: f.kenshoGoogleSwitch?.enabled ?? false,

            // Workplace integrations (nested)
            workplaceIntegrations: {
                enabled:    f.workplaceIntegrations?.enabled                                ?? false,
                teams:      f.workplaceIntegrations?.integrations?.teams?.enabled       ?? false,
                outlook:    f.workplaceIntegrations?.integrations?.outlook?.enabled     ?? false,
                sharepoint: f.workplaceIntegrations?.integrations?.sharepoint?.enabled  ?? false,
                onedrive:   f.workplaceIntegrations?.integrations?.onedrive?.enabled    ?? false,
                ado:        f.workplaceIntegrations?.integrations?.ado?.enabled         ?? false,
                calendar:   f.workplaceIntegrations?.integrations?.calendar?.enabled    ?? true
            },

            // Spark Store (nested)
            sparkStore: {
                enabled:   f.sparkStore?.enabled                        ?? false,
                embedIcon: f.sparkStore?.features?.embedIcon?.enabled   ?? false
            },

            // Sharing (nested)
            sharing: {
                enabled:  f.sharing?.enabled                  ?? false,
                email:    f.sharing?.options?.email?.enabled   ?? false,
                teams:    f.sharing?.options?.teams?.enabled   ?? false,
                onedrive: f.sharing?.options?.onedrive?.enabled ?? false,
                ado:      f.sharing?.options?.ado?.enabled      ?? false
            },

            // Spark Widget (nested)
            sparkWidget: {
                enabled:        f.sparkWidget?.enabled                              ?? false,
                authentication: f.sparkWidget?.features?.authentication?.enabled   ?? false,
                sparkPicker:    f.sparkWidget?.features?.sparkPicker?.enabled       ?? false
            },

            // Analytics Dashboard icon
            analyticsDashboard: f.analyticsDashboard?.enabled ?? true,

            // Metadata
            environment: data?.environment?.name  || 'unknown',
            version:     data?.metadata?.version  || '1.0.0'
        };
    } catch (err) {
        console.error('[FeatureFlags] Parse error:', err);
        return DEFAULT_FLAGS;
    }
}

// ── Utility Hook: Check if ANY workplace integration is enabled ───────────────
export function useHasAnyIntegration() {
    const flags = useFeatureFlags();
    
    if (!flags.workplaceIntegrations.enabled) {
        return false;
    }
    
    return flags.workplaceIntegrations.teams ||
           flags.workplaceIntegrations.outlook ||
           flags.workplaceIntegrations.sharepoint ||
           flags.workplaceIntegrations.onedrive ||
           flags.workplaceIntegrations.ado;
}

// ── Utility Hook: Check if ANY sharing option is enabled ──────────────────────
export function useHasAnySharing() {
    const flags = useFeatureFlags();
    
    if (!flags.sharing.enabled) {
        return false;
    }
    
    return flags.sharing.email ||
           flags.sharing.teams ||
           flags.sharing.onedrive ||
           flags.sharing.ado;
}

// ── Utility Hook: Get enabled integrations as array ───────────────────────────
export function useEnabledIntegrations() {
    const flags = useFeatureFlags();
    
    if (!flags.workplaceIntegrations.enabled) {
        return [];
    }
    
    const enabled = [];
    
    if (flags.workplaceIntegrations.teams) enabled.push('teams');
    if (flags.workplaceIntegrations.outlook) enabled.push('outlook');
    if (flags.workplaceIntegrations.sharepoint) enabled.push('sharepoint');
    if (flags.workplaceIntegrations.onedrive) enabled.push('onedrive');
    if (flags.workplaceIntegrations.ado) enabled.push('ado');
    
    return enabled;
}

// ── Utility Hook: Get enabled sharing options as array ────────────────────────
export function useEnabledSharingOptions() {
    const flags = useFeatureFlags();
    
    if (!flags.sharing.enabled) {
        return [];
    }
    
    const enabled = [];
    
    if (flags.sharing.email) enabled.push('email');
    if (flags.sharing.teams) enabled.push('teams');
    if (flags.sharing.onedrive) enabled.push('onedrive');
    if (flags.sharing.ado) enabled.push('ado');
    
    return enabled;
}
